import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  type CitySuggestion,
} from '@cibli/shared';
import { ArrowLeft, MapPin } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconButton from '@/features/common/components/IconButton.tsx';
import MainLayout from '@/features/common/components/MainLayout';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { fetchCitySuggestions, resolveBaseCountry, validateCityZipcode } from '@/services/address';

import type { UserInfo } from '../context/register.context';

import FormButtons from '../components/FormButtons';
import { useRegisterContext } from '../hooks/useRegisterContext';

interface Props {
  onCancel: () => void;
  onBack?: () => void;
  onNext: (data?: Partial<UserInfo>) => void | Promise<void>;
}

export default function CityScreen({ onCancel, onBack, onNext }: Props) {
  const { t, i18n } = useTranslation();
  const { cityForm, namesForm } = useRegisterContext();
  const { firstName } = namesForm.watch();
  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Guidance shown when no suggestion matches, or after selecting a city
  // without postal code (GH-598: explain what to do next, in French).
  const [bottomHint, setBottomHint] = useState<string | null>(null);
  const [inputDisplayValue, setInputDisplayValue] = useState('');
  // Plan 2026-10-21 : vérification indisponible → réessai (1), puis
  // « vérifiez vos saisies » (2), la 3e validation continue en NON VÉRIFIÉ
  // (jamais mis en cache, jamais présenté comme vérifié).
  const unavailableAttemptsRef = useRef(0);

  useEffect(() => {
    const { city, zipcode } = cityForm.getValues();
    if (city && zipcode) {
      setInputDisplayValue(`${city} (${zipcode})`);
    }
  }, [cityForm]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Sequence guard: a slow response must never overwrite a newer one.
  const suggestionsSeqRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Locale de base de l'appareil (GH-599 / plan 2026-10-21) : pilote la
  // recherche — la langue d'affichage peut changer sans influencer les
  // résultats. La LOCALE configurée (cabine ou région web) prime sur le pays
  // physique : une base es-MX cherche au Mexique depuis la France.
  const { booth, webRegionConfig } = useBooth();
  const countryHint = resolveBaseCountry({
    boothDefaultLocaleId: booth?.defaultLocaleId,
    webRegionDefaultLocale: webRegionConfig?.defaultLocale?.codeLong,
    webRegionCountry: webRegionConfig?.countryCode,
  });
  // Langue de l'interface complète (es-MX, en-GB…) : noms de lieux localisés
  // côté providers externes (repli mondial).
  const uiLang = i18n.language || 'fr';

  const parseCityZipcode = useCallback((value: string) => {
    // Pattern "City (12345)" or "City (01000)" or "City (CP1234)"
    const match = value.match(/^(.+?)\s*\(([A-Za-z0-9\- ]{3,10})\)$/);
    if (match) {
      return { city: match[1].trim(), zipcode: match[2].trim() };
    }
    // Pattern "City 101000" (without parentheses, e.g. after a city-only pick)
    const spaced = value.match(/^(.+?)\s+(\d{4,7})$/);
    if (spaced) {
      return { city: spaced[1].trim(), zipcode: spaced[2] };
    }
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length >= 4 && digitsOnly.length <= 7) {
      return { city: '', zipcode: digitsOnly };
    }
    return null;
  }, []);

  // Vrai = le candidat peut continuer avec un couple NON VÉRIFIÉ.
  const handleUnavailable = useCallback(() => {
    const attempt = unavailableAttemptsRef.current;
    if (attempt === 0) {
      unavailableAttemptsRef.current = 1;
      setValidationError(t('formErrors.verifyUnavailableRetry'));
      return false;
    }
    if (attempt === 1) {
      unavailableAttemptsRef.current = 2;
      setValidationError(t('formErrors.verifyUnavailableCheck'));
      return false;
    }
    setValidationError(null);
    return true;
  }, [t]);

  const handleSubmit = useCallback(async () => {
    setValidationError(null);
    const { city: formCity, zipcode } = cityForm.getValues();
    let city = formCity;

    if (!zipcode) {
      setValidationError(t('formErrors.required'));
      return;
    }

    setIsValidating(true);
    try {
      if (!city) {
        const suggestions = await fetchCitySuggestions(zipcode, 1, countryHint, uiLang);
        if (suggestions.length > 0) {
          // Ville tirée des suggestions du code postal : couple confirmé par
          // la source elle-même (aucune mise en cache — plan 2026-10-21).
          city = suggestions[0].city;
          cityForm.setValue('city', city);
          void onNext({ city, zipcode });
          return;
        }
        // Illectronisme: If user entered a valid zipcode but no matching city found, don't crash
        setValidationError(t('formErrors.invalidCityZipcode'));
        return;
      }

      const status = await validateCityZipcode(city, zipcode, countryHint);
      if (status === 'invalid') {
        setValidationError(t('formErrors.invalidCityZipcode'));
        return;
      }
      if (status === 'unavailable') {
        if (handleUnavailable()) {
          void onNext({ city, zipcode });
        }
        return;
      }
      void onNext({ city, zipcode });
    } catch (error) {
      console.error('City validation failed:', error);
      // Échec de requête : même flux réessai → saisie manuelle, jamais de
      // passage silencieux (plan 2026-10-21).
      if (city && zipcode && handleUnavailable()) {
        void onNext({ city, zipcode });
      } else if (!(city && zipcode)) {
        setValidationError(t('common.error.something_went_wrong'));
      }
    } finally {
      setIsValidating(false);
    }
  }, [cityForm, countryHint, uiLang, handleUnavailable, onNext, t]);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputDisplayValue(value);
      setValidationError(null);
      setBottomHint(null);
      unavailableAttemptsRef.current = 0;

      const parsed = parseCityZipcode(value);
      if (parsed) {
        cityForm.setValue('city', parsed.city, { shouldValidate: true });
        cityForm.setValue('zipcode', parsed.zipcode, { shouldValidate: true });
      } else {
        // Saisie libre = la ville affichée ; l'ancien code postal ne doit
        // jamais rester attaché (plan 2026-10-21 : affiché = soumis).
        cityForm.setValue('city', value.trim(), { shouldValidate: true });
        cityForm.setValue('zipcode', '', { shouldValidate: true });
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      const trimmed = value.trim();
      if (trimmed.length < 1) {
        suggestionsSeqRef.current += 1;
        setIsLoading(false);
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        const seq = ++suggestionsSeqRef.current;
        setIsLoading(true);
        try {
          const results = await fetchCitySuggestions(value, 15, countryHint, uiLang);
          // Ignore stale responses (a newer keystroke already started a fetch).
          if (seq !== suggestionsSeqRef.current) return;
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
          if (results.length === 0 && trimmed.length >= 2) {
            setBottomHint(t('FormScreen.City.noSuggestions', { query: trimmed }));
          }
        } finally {
          if (seq === suggestionsSeqRef.current) {
            setIsLoading(false);
          }
        }
      }, 350);
    },
    [cityForm, parseCityZipcode, countryHint, uiLang, t],
  );

  const handleSuggestionSelect = useCallback(
    (suggestion: CitySuggestion) => {
      suggestionsSeqRef.current += 1;
      unavailableAttemptsRef.current = 0;
      cityForm.setValue('city', suggestion.city, { shouldValidate: true });
      cityForm.setValue('zipcode', suggestion.zipcode, { shouldValidate: true });
      setInputDisplayValue(
        suggestion.zipcode ? `${suggestion.city} (${suggestion.zipcode})` : suggestion.city,
      );
      setSuggestions([]);
      setShowSuggestions(false);
      // City without postal code: tell the user the postal code is still needed.
      setBottomHint(suggestion.zipcode ? null : t('FormScreen.City.addPostalCode'));
      inputRef.current?.blur();
    },
    [cityForm, t],
  );

  const handleInputFocus = useCallback(() => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [suggestions.length]);

  const handleInputBlur = useCallback(() => {
    const parsed = parseCityZipcode(inputDisplayValue);
    if (parsed) {
      cityForm.setValue('city', parsed.city, { shouldValidate: true });
      cityForm.setValue('zipcode', parsed.zipcode, { shouldValidate: true });
    } else {
      cityForm.setValue('city', inputDisplayValue.trim(), { shouldValidate: true });
      cityForm.setValue('zipcode', '', { shouldValidate: true });
    }
    setTimeout(() => setShowSuggestions(false), 200);
  }, [inputDisplayValue, cityForm, parseCityZipcode]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <MainLayout
      className="gap-4 sm:gap-6 text-center"
      onClose={onCancel}
      rightContent={
        onBack ? (
          <IconButton
            className="ml-6"
            icon={<ArrowLeft strokeWidth={3} className="min-h-6 min-w-6" />}
            onClick={onBack}
          />
        ) : undefined
      }
    >
      <h3 className="text-lg sm:text-xl text-primary">
        {t('FormScreen.City.hint', { name: firstName })}
      </h3>
      <h1 className="text-3xl md:text-4xl lg:text-5xl text-balance">
        {t('FormScreen.City.title')}
      </h1>
      <p className="text-lg md:text-xl lg:text-2xl max-w-xl text-balance text-muted-foreground">
        {t('FormScreen.City.description')}
      </p>

      <Form {...cityForm}>
        <form
          onSubmit={cityForm.handleSubmit(handleSubmit)}
          className="w-full flex flex-col gap-4 max-w-md"
        >
          <FormField
            control={cityForm.control}
            name="zipcode"
            render={() => (
              <FormItem className="mt-4 relative">
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
                  <FormControl>
                    <Input
                      size="xl"
                      ref={inputRef}
                      value={inputDisplayValue}
                      placeholder={t('FormScreen.City.cityZipcodePlaceholder')}
                      type="text"
                      maxLength={60}
                      className="pl-12"
                      onClick={() => inputRef.current?.focus()}
                      onChange={e => {
                        handleInputChange(e.target.value);
                      }}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={cityForm.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input size="xl" {...field} type="hidden" />
                </FormControl>
              </FormItem>
            )}
          />

          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-20 w-full max-w-md mt-24 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.city}-${suggestion.zipcode}-${index}`}
                  type="button"
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                  onClick={() => handleSuggestionSelect(suggestion)}
                >
                  <span className="font-medium">{suggestion.city}</span>
                  {suggestion.zipcode && (
                    <span className="text-muted-foreground ml-2">({suggestion.zipcode})</span>
                  )}
                  {suggestion.country && (
                    <span className="text-muted-foreground ml-2">· {suggestion.country}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          <FormButtons
            showBackButton={!!onBack}
            onBack={onBack}
            stepName="city"
            disableNextButton={isValidating}
          />

          <div className="text-sm text-center min-h-6">
            {isValidating ? (
              <span className="text-muted-foreground">{t('FormScreen.City.validating')}</span>
            ) : isLoading ? (
              <span className="text-muted-foreground">{t('FormScreen.City.loading')}</span>
            ) : validationError ? (
              <span role="alert" className="text-destructive">
                {validationError}
              </span>
            ) : bottomHint ? (
              <span className="text-muted-foreground">{bottomHint}</span>
            ) : null}
          </div>
        </form>
      </Form>
    </MainLayout>
  );
}
